import type { Gathering } from '@cobu/types';

type RateDetails = Gathering['rateDetails'];

/**
 * `rateDetails` is an untyped JSON blob server-side, so every read here is
 * guarded: the top level can be null, `totalAmount` can be absent/null, and
 * `rates[].amount` arrives as a decimal string ("1.00"), not a number.
 */

const toAmount = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Number.parseFloat(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Price the resident pays to attend, or null when the event is free / has no
 * usable rate data. Prefers `totalAmount`; falls back to summing `rates[]`
 * when the total is missing but line items are present.
 */
export const getEventFee = (rateDetails: RateDetails): number | null => {
  if (!rateDetails) return null;

  const total = toAmount(rateDetails.totalAmount);
  if (total != null && total > 0) return total;

  const rates = rateDetails.rates;
  if (!Array.isArray(rates) || rates.length === 0) return null;

  const sum = rates.reduce<number>((acc, rate) => acc + (toAmount(rate?.amount) ?? 0), 0);
  return sum > 0 ? sum : null;
};

/** `10` -> "$10", `10.5` -> "$10.50". */
export const formatEventFee = (amount: number): string =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;

export const isPaidEvent = (rateDetails: RateDetails): boolean =>
  getEventFee(rateDetails) != null;
